import { isMarketplaceBucketItem, TCreateSendBucketItem, TMarketplaceBucketItem } from '../types/bucket';
import {
  IMarketplaceRequestItem,
  MARKETPLACE_CAMPAIGN_ID,
  MARKETPLACE_CURRENCY_CODE,
  MARKETPLACE_PRODUCT_ID_PREFIX,
  TMarketplaceRequestMap,
} from '../types/marketplace';

export const getMarketplaceProductId = (item: TMarketplaceBucketItem): string => {
  const raw = item.marketplace_product_id || item.item_id;

  return raw.startsWith(MARKETPLACE_PRODUCT_ID_PREFIX) ? raw : `${MARKETPLACE_PRODUCT_ID_PREFIX}${raw}`;
};

export const mapMarketplaceItemToRequest = (item: TMarketplaceBucketItem): IMarketplaceRequestItem => ({
  product_id: getMarketplaceProductId(item),
  name: item.name,
  brand: item.vendor,
  // The band's top. `mapCatalogItem` already resolves `price` to it; the fallback covers picks
  // persisted in drafts saved before it did.
  price: item.max_price ?? item.price,
  currency: item.currency_code || MARKETPLACE_CURRENCY_CODE,
  image_url: item.image_url,
  description: item.description,
  quantity: item.quantity ?? 1,
});

/**
 * The `marketplace` map for a send: every marketplace item in it, PYG pre-select or
 * custom-cart line alike, keyed by its `product_id`. `null` when the send has none — the
 * contract distinguishes "no marketplace content" from "an empty map".
 */
export const buildMarketplaceRequestMap = (items: TMarketplaceBucketItem[]): TMarketplaceRequestMap | null => {
  if (!items.length) {
    return null;
  }

  return items.reduce<TMarketplaceRequestMap>((map, item) => {
    const entry = mapMarketplaceItemToRequest(item);

    return { ...map, [entry.product_id]: entry };
  }, {});
};

/**
 * Splits a mixed bucket into the IE items that stay in `items[]` and the marketplace picks.
 *
 * Generic in the IE half so each caller keeps its own narrower item type: a negated type guard
 * does not narrow on its own under TS 4.6, which would otherwise widen `ieItems` back to the
 * full union at every call site.
 */
export const splitBucketBySource = <TIeItem>(
  items: (TIeItem | TMarketplaceBucketItem)[] | null | undefined,
): { ieItems: TIeItem[]; marketplaceItems: TMarketplaceBucketItem[] } => {
  const all = items ?? [];

  return {
    ieItems: all.filter((item): item is TIeItem => !isMarketplaceBucketItem(item as TCreateSendBucketItem)),
    marketplaceItems: all.filter((item): item is TMarketplaceBucketItem =>
      isMarketplaceBucketItem(item as TCreateSendBucketItem),
    ),
  };
};

export const resolveLooseContentsEngagementId = (
  preCreatedBoxId: string | undefined,
  ieItemCount: number,
  hasMarketplaceContext: boolean,
): string => (hasMarketplaceContext && ieItemCount === 0 ? MARKETPLACE_CAMPAIGN_ID : preCreatedBoxId ?? '');
